// Print/PDF view for a customer's statement of account. Mirrors the other
// *Print components (company header, document title, party block, table,
// totals, prepared-by) so the rendered PDF matches the rest of the app.
import { COMPANY_PDF_LOGO } from "@/lib/brand";
import { formatLedgerBalance } from "@/lib/format";

export interface LedgerPrintRow {
  date: string;
  type: "invoice" | "receipt" | "reversal" | "other";
  reference: string;
  description: string;
  debit: number;
  credit: number;
  running: number;
}

export interface LedgerPrintData {
  customerName: string;
  dateFrom?: string | null;
  dateTo?: string | null;
  openingBalance: number;
  rows: LedgerPrintRow[];
  totals: {
    total_debit: number;
    total_credit: number;
    closing_balance: number;
  };
}

export interface LedgerPrintCompany {
  name: string;
  address: string;
  phone: string;
  email: string;
  currencySymbol?: string;
}

function money(n: number) {
  return Number(n || 0).toLocaleString("en-GH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// dd-mm-yyyy formatting to match the other documents (03-06-2026).
function formatDmy(input?: string | null) {
  if (!input) return "";
  const s = String(input);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
}

export default function CustomerLedgerPrint({
  data,
  company,
  preparedBy = "System",
}: {
  data: LedgerPrintData;
  company: LedgerPrintCompany;
  preparedBy?: string;
}) {
  const cur = company.currencySymbol || "GH₵";
  const showOpening = Boolean(data.dateFrom);

  const periodLabel = (() => {
    const from = formatDmy(data.dateFrom);
    const to = formatDmy(data.dateTo);
    if (from && to) return `${from} — ${to}`;
    if (from) return `From ${from}`;
    if (to) return `Up to ${to}`;
    return "All transactions";
  })();

  return (
    <div
      className="print-area relative bg-white text-slate-900 p-5 font-sans overflow-hidden"
      style={{ width: "800px" }}
    >
      <div className="flex justify-between items-start border-b-2 border-slate-300 pb-2">
        <div className="flex items-center gap-4">
          <img
            src={COMPANY_PDF_LOGO}
            alt="Company logo"
            className="h-20 w-auto object-contain"
          />
          <div>
            <div className="text-lg font-bold uppercase leading-tight">
              {company.name}
            </div>
            {company.address ? (
              <div className="text-[11px] mt-0.5">{company.address}</div>
            ) : null}
            {company.phone ? (
              <div className="text-[11px]">Tel: {company.phone}</div>
            ) : null}
            {company.email ? (
              <div className="text-[11px]">Email: {company.email}</div>
            ) : null}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold tracking-wide leading-tight">
            STATEMENT OF ACCOUNT
          </div>
          <div className="text-[11px] mt-0.5">{periodLabel}</div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 text-[13px] leading-tight">
        <div>
          <span className="text-[10px] uppercase text-slate-500">Account: </span>
          <span className="font-semibold">{data.customerName}</span>
        </div>
        <div className="text-right">
          <span className="text-[10px] uppercase text-slate-500">
            Outstanding Balance:{" "}
          </span>
          <span className="font-bold">
            {cur} {formatLedgerBalance(data.totals.closing_balance, money)}
          </span>
        </div>
      </div>

      <table className="w-full mt-3 text-[11px] border-collapse">
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-slate-300 px-2 py-1.5 text-left w-24">
              DATE
            </th>
            <th className="border border-slate-300 px-2 py-1.5 text-left">
              DESCRIPTION
            </th>
            <th className="border border-slate-300 px-2 py-1.5 text-right w-28">
              DEBIT
            </th>
            <th className="border border-slate-300 px-2 py-1.5 text-right w-28">
              CREDIT
            </th>
            <th className="border border-slate-300 px-2 py-1.5 text-right w-28">
              BALANCE
            </th>
          </tr>
        </thead>
        <tbody>
          {showOpening ? (
            <tr className="bg-slate-50">
              <td className="border border-slate-300 px-2 py-1.5 align-middle">
                {formatDmy(data.dateFrom)}
              </td>
              <td className="border border-slate-300 px-2 py-1.5 italic align-middle">
                Opening Balance
              </td>
              <td className="border border-slate-300 px-2 py-1.5 text-right align-middle">
                —
              </td>
              <td className="border border-slate-300 px-2 py-1.5 text-right align-middle">
                —
              </td>
              <td className="border border-slate-300 px-2 py-1.5 text-right font-mono font-semibold align-middle">
                {formatLedgerBalance(data.openingBalance, money)}
              </td>
            </tr>
          ) : null}
          {data.rows.length === 0 && !showOpening ? (
            <tr>
              <td
                className="border border-slate-300 px-2 py-4 text-center text-slate-500 align-middle"
                colSpan={5}
              >
                No transactions for this period.
              </td>
            </tr>
          ) : null}
          {data.rows.map((r, i) => (
            <tr key={i}>
              <td className="border border-slate-300 px-2 py-1.5 whitespace-nowrap align-middle">
                {formatDmy(r.date)}
              </td>
              <td className="border border-slate-300 px-2 py-1.5 align-middle">
                {r.description}
              </td>
              <td className="border border-slate-300 px-2 py-1.5 text-right font-mono align-middle">
                {r.debit > 0 ? money(r.debit) : "—"}
              </td>
              <td className="border border-slate-300 px-2 py-1.5 text-right font-mono align-middle">
                {r.credit > 0 ? money(r.credit) : "—"}
              </td>
              <td className="border border-slate-300 px-2 py-1.5 text-right font-mono font-semibold align-middle">
                {formatLedgerBalance(r.running, money)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-slate-100">
            <td
              className="border border-slate-300 px-2 py-1.5 text-right font-semibold align-middle"
              colSpan={2}
            >
              TOTALS
            </td>
            <td className="border border-slate-300 px-2 py-1.5 text-right font-mono font-semibold align-middle">
              {money(data.totals.total_debit)}
            </td>
            <td className="border border-slate-300 px-2 py-1.5 text-right font-mono font-semibold align-middle">
              {money(data.totals.total_credit)}
            </td>
            <td className="border border-slate-300 px-2 py-1.5 text-right font-mono font-bold align-middle">
              {formatLedgerBalance(data.totals.closing_balance, money)}
            </td>
          </tr>
          <tr>
            <td
              className="border border-slate-300 px-2 py-1.5 text-right font-bold uppercase align-middle"
              colSpan={4}
            >
              Outstanding Balance ({cur})
            </td>
            <td className="border border-slate-300 px-2 py-1.5 text-right font-mono font-bold align-middle">
              {formatLedgerBalance(data.totals.closing_balance, money)}
            </td>
          </tr>
        </tfoot>
      </table>

      <div className="mt-3 pt-2 border-t border-slate-200 grid grid-cols-2 gap-8 text-[11px]">
        <div>
          <div className="text-slate-500 uppercase tracking-wide">
            Prepared By
          </div>
          <div className="font-semibold mt-0.5">{preparedBy}</div>
          <div className="mt-6 border-b border-slate-400" />
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-wide">
            Authorised Signature
          </div>
          <div className="mt-6 border-b border-slate-400" />
        </div>
      </div>
    </div>
  );
}
